import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Crown } from 'lucide-react';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Timeout safety - prevent infinite loading state
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      toast.error('Request timed out. Please try again.');
    }, 20000);

    try {
      if (isLogin) {
        await login(email, password);
        clearTimeout(loadingTimeout);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        await register({ email, password, name });
        clearTimeout(loadingTimeout);
        toast.success('Account created! Welcome to Temple of Children.');
        navigate('/dashboard');
      }
    } catch (error) {
      clearTimeout(loadingTimeout);
      // More specific error messages
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Connection timed out. Please check your internet and try again.');
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('Unable to connect to server. Please try again later.');
      } else if (error.response?.status === 401) {
        toast.error('Invalid email or password.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Invalid request. Please check your input.');
      } else {
        toast.error(error.response?.data?.detail || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 relative">
      {/* Background Stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
            }}
          />
        ))}
      </div>

      <button
        data-testid="back-to-home-btn"
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 text-slate-400 hover:text-white transition-colors flex items-center gap-2"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Home</span>
      </button>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-panel rounded-3xl p-8">
          <h2 className="text-3xl font-philosopher font-bold text-center mb-2 glow-text">
            {isLogin ? 'Welcome Back' : 'Join the Temple'}
          </h2>
          <p className="text-slate-400 text-center mb-2">
            {isLogin ? 'Parent Portal' : 'Create your family account'}
          </p>
          
          {/* Founding Families Banner (Signup only) */}
          {!isLogin && (
            <div className="flex items-center justify-center gap-2 mb-6 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-300">Now open to Founding Families</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Name</label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                  placeholder="Your name"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Free Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-slate-400 hover:text-white transition-colors text-sm"
            >
              {isLogin ? "New here? Create an account" : 'Already have an account? Sign in'}
            </button>
          </div>
          
          {/* Soft launch info */}
          {!isLogin && (
            <div className="mt-4 text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                <Sparkles className="w-3 h-3 text-indigo-400" />
                <span>Free tier: 5 activities per day per child</span>
              </div>
              <p className="text-xs text-amber-400/80">
                Become a Founding Family for unlimited access
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
